import { Elastic, Power3, Sine, TimelineLite, TimelineMax, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import { Emitter } from "pixi-particles";
import { Application } from "../Application";
import { BalloonControlToken } from "../Data/BalloonControlToken";
import * as Constants from "../Data/Constants";
import { PowerupType } from "../Data/Powerups";
import * as SFX from "../Data/SoundEffectKeys";
import * as Topics from "../Data/Topics";
import * as VoiceOver from "../Data/VoiceOverKeys";
import { BalloonParameter } from "../GameObjects/DebugBalloonControls";
import { InteruptType, VoiceCategory } from "../GameObjects/VoiceOverManager";
import { BoxCollider } from "../Physics/BoxCollider";
import { IPhysicsBody } from "../Physics/IPhysicsBody";

enum InvulnType {
    DAMAGE,
    POWERUP,
    NUM_ELEMENTS,
}

export class Balloon extends PIXI.Container implements IPhysicsBody {
    public static readonly SPEED_BOOST_MULT: number = 1.75;
    public static readonly DEFAULT_X: number = -350;

    private static readonly LIFT_DEFAULT: number = 1000;
    private static readonly GRAVITY_DEFAULT: number = -350;
    private static readonly SPEED_MIN_DEFAULT: number = -200;
    private static readonly SPEED_MAX_DEFAULT: number = 200;

    private static readonly FLAME_ANIM: string = "FlameAnim";
    private static readonly HEALTH_MAX: number = 5;
    private static readonly DAMAGE_INVULN_DUR: number = 2;
    private static readonly POWER_INVULN_DUR: number = 8;
    private static readonly SCALE_SMALL: number = 0.55;
    private static readonly SCALE_NORM: number = 0.7;
    private static readonly SCALE_LARGE: number = 0.9;
    private static readonly SHRINK_DUR: number = 8;
    private static readonly SPEED_BOOST_DUR: number = 5;
    private static readonly HEIGHT_MIN: number = -300;
    private static readonly HEIGHT_MAX: number = 215;
    private static readonly FLAME_POS: PIXI.Point[] = [
        new PIXI.Point(0, 89),
        new PIXI.Point(0, 98),
        new PIXI.Point(0, 103),
        new PIXI.Point(0, 98),
        new PIXI.Point(0, 111),
        new PIXI.Point(0, 82),
        new PIXI.Point(0, 103),
        new PIXI.Point(0, 88),
    ];

    private _collider: BoxCollider;
    private _colliderNorm: BoxCollider;
    private _colliderSmall: BoxCollider;
    private _colliderLarge: BoxCollider;
    private _active: boolean;
    private _velocity: number;
    private _lift: number;
    private _gravity: number;
    private _speedMin: number;
    private _speedMax: number;
    private _isLifting: boolean;
    private _health: number;
    private _invulnerable: boolean;
    private _isAnimating: boolean;
    private _size: PIXI.Point;

    private _sprite: PIXI.Sprite;
    private _sparkles: Emitter;
    private _particleContainer: PIXI.Container;
    private _heartContainer: PIXI.Container;
    private _heartLeft: PIXI.Sprite;
    private _heartRight: PIXI.Sprite;
    private _flame: HLF.AnimatedSprite;

    private _invulnTween: TimelineMax | undefined;
    private _sizeModDelay: TimelineLite | undefined;
    private _sizeModTween: TweenLite | undefined;
    private _speedBoostDelay: TimelineLite | undefined;
    private _posTween: TimelineLite | undefined;
    private _heartTween: TimelineLite | undefined;
    private _flameTween: TweenLite | undefined;
    private _subscriptions: any[];

    constructor(pIndex: number) {
        super();
        this._isLifting = false;
        this._invulnerable = false;
        this._active = true;
        this._isAnimating = false;
        this._health = Balloon.HEALTH_MAX;
        this._size = new PIXI.Point();

        this.scale.set(Balloon.SCALE_NORM);

        this._sprite = HLF.PixiUtils.makeSprite(
            "balloon-" + HLF.NumberUtils.addZeroPadding(pIndex, 2),
            Constants.SPRITESHEET_GAMEPLAY,
        );
        this.addChild(this._sprite);

        this._flame = new HLF.AnimatedSprite();
        this._flame.addAnimation( Balloon.FLAME_ANIM, new HLF.SpriteAnimation(
            "flame-", Constants.SPRITESHEET_GAMEPLAY, 5, 2, 12, true,
        ));
        this._flame.anchor.set(0.5, 1);
        this._flame.position.copy(Balloon.FLAME_POS[pIndex]);
        this._flame.alpha = 0;
        this._flame.play(Balloon.FLAME_ANIM);
        this._sprite.addChild(this._flame);

        this._heartContainer = new PIXI.Container();
        this._heartContainer.position.set(0, -15);
        this._heartContainer.visible = false;
        this.addChild(this._heartContainer);

        this._heartLeft = HLF.PixiUtils.makeSprite("heart-left", Constants.SPRITESHEET_GAMEPLAY);
        this._heartContainer.addChild(this._heartLeft);

        this._heartRight = HLF.PixiUtils.makeSprite("heart-right", Constants.SPRITESHEET_GAMEPLAY);
        this._heartContainer.addChild(this._heartRight);

        this._particleContainer = new PIXI.Container();
        this.addChild(this._particleContainer);

        this._sparkles = new Emitter(
            this._particleContainer,
            [ PIXI.loader.resources[Constants.SPRITESHEET_UI].textures!["particle-sparkle"] ],
            {
                alpha: {
                    list: [
                        { value: 0.1, time: 0 },
                        { value: 0.8, time: 0.1 },
                        { value: 0.1, time: 1 },
                    ],
                    isStepped: false,
                },
                scale: {
                    list: [
                        { value: 0.4, time: 0.0 },
                        { value: 1.0, time: 0.2 },
                        { value: 0.4, time: 1.0 },
                    ],
                },
                rotationSpeed: { min: -80, max: 80 },
                lifetime: { min: 0.75, max: 0.75 },
                frequency: 0.15,
                spawnChance: 1,
                particlesPerWave: 1,
                // emitterLifetime: 1.0,
                maxParticles: 50,
                pos: { x: 0, y: 0 },
                addAtBack: false,
                spawnType: "circle",
                spawnCircle: { x: 0, y: 0, r: 120 },
            },
        );
        this._sparkles.emit = false;

        this._colliderNorm = new BoxCollider(-40, 40, -70, 25);
        const scaleSmall: number = (Balloon.SCALE_SMALL / Balloon.SCALE_NORM);
        const scaleLarge: number = (Balloon.SCALE_LARGE / Balloon.SCALE_NORM);
        this._colliderSmall = new BoxCollider(
            this._colliderNorm.left * scaleSmall,
            this._colliderNorm.right * scaleSmall,
            this._colliderNorm.top * scaleSmall,
            this._colliderNorm.bottom * scaleSmall,
        );
        this._colliderLarge = new BoxCollider(
            this._colliderNorm.left * scaleLarge,
            this._colliderNorm.right * scaleLarge,
            this._colliderNorm.top * scaleLarge,
            this._colliderNorm.bottom * scaleLarge,
        );
        this._collider = this._colliderNorm;

        this._velocity = 0;
        this._lift = Balloon.LIFT_DEFAULT;
        this._gravity = Balloon.GRAVITY_DEFAULT;
        this._speedMin = Balloon.SPEED_MIN_DEFAULT;
        this._speedMax = Balloon.SPEED_MAX_DEFAULT;

        this._subscriptions = new Array<any>();
        this._subscriptions.push(PubSub.subscribe(
            Topics.ON_DEBUG_BALLOON_PARAM_CHANGED,
            this.onParamChanged.bind(this),
        ));
        this._subscriptions.push(PubSub.subscribe(
            Topics.BALLOON_LIFT_START,
            this.startLift.bind(this),
        ));
        this._subscriptions.push(PubSub.subscribe(
            Topics.BALLOON_LIFT_END,
            this.stopLift.bind(this),
        ));
    }

    public set isLifting(pValue: boolean) {
        this._isLifting = pValue;
    }

    public get active(): boolean {
        return this._active;
    }

    public set active(pValue: boolean) {
        this._active = pValue;
    }

    public get collider(): BoxCollider {
        return this._collider;
    }

    public get visualPos(): PIXI.Point {
        return this._sprite.getGlobalPosition();
    }

    public get invulnerable(): boolean {
        return this._invulnerable;
    }

    private get acceleration(): number {
        if (this._isLifting && this._health > 0) {
            return this._lift;
        }
        else {
            return this._gravity;
        }
    }

    public update(pDeltaTime: number): void {
        if (this._isAnimating === false) {
            this._velocity += -this.acceleration * pDeltaTime;
            // TODO:SH: Fix the use of negatives (due to webGL top = 0)
            this._velocity = HLF.MathUtils.clamp(this._velocity, -this._speedMax, -this._speedMin);
            this.y += this._velocity * pDeltaTime;
            if (this.y > Balloon.HEIGHT_MAX) {
                this.y = Balloon.HEIGHT_MAX;
                this._velocity = 0;
                if (this._active) {
                    this.onGroundHit();
                }
            }
            else if (this.y < Balloon.HEIGHT_MIN) {
                this.y = Balloon.HEIGHT_MIN;
                this._velocity = 0;
            }
        }
        this._sparkles.update(pDeltaTime);
        this._flame.update(pDeltaTime);
    }

    public onResize(pSize: PIXI.Point): void {
        this._size.copy(pSize);
    }

    public destroy(pOptions: boolean | PIXI.DestroyOptions = { children: true }): void {
        super.destroy(pOptions);
        for (let i = 0; i < this._subscriptions.length; ++i) {
            PubSub.unsubscribe(this._subscriptions[i]);
        }
        if (this._invulnTween !== undefined) { this._invulnTween.kill(); }
        if (this._sizeModDelay !== undefined) { this._sizeModDelay.kill(); }
        if (this._sizeModTween !== undefined) { this._sizeModTween.kill(); }
        if (this._speedBoostDelay !== undefined) { this._speedBoostDelay.kill(); }
        if (this._posTween !== undefined) { this._posTween.kill(); }
        if (this._heartTween !== undefined) { this._heartTween.kill(); }
        if (this._flameTween !== undefined) { this._flameTween.kill(); }
    }

    public onPowerupCollected(pType: PowerupType): void {
        switch (pType) {
            case PowerupType.SPEED:
                this.boostSpeed();
                this.playSfx(SFX.POWERUP_SPEED);
                break;
            case PowerupType.HEALTH:
                this.modifyHealth(1);
                this.playSfx(SFX.POWERUP_HEALTH);
                break;
            case PowerupType.INVULN:
                this.setInvulnerable(Balloon.POWER_INVULN_DUR, InvulnType.POWERUP);
                this.playSfx(SFX.POWERUP_SHIELD);
                break;
            case PowerupType.SHRINK:
                this.modifySize(Balloon.SCALE_SMALL, this._colliderSmall, Balloon.SHRINK_DUR);
                this.playSfx(SFX.POWERUP_SHRINK);
                break;
        }
    }

    public onObstacleHit(): void {
        if (this._invulnerable === false) {
            this.modifyHealth(-1);
            this.animateDamage();
            if (this._health > 0) {
                Application.instance.voiceOver.play(
                    HLF.ArrayUtils.random(VoiceOver.COLLISION),
                    InteruptType.LESS,
                    VoiceCategory.GAMEPLAY,
                );
            }
        }
    }

    public onGroundHit(): void {
        if (this._invulnerable === false) {
            this.modifyHealth(-1);
            this.animateDamage();
            this.playSfx(SFX.IMPACT_DINOSAUR);
            if (this._health > 0) {
                Application.instance.voiceOver.play(
                    VoiceOver.COLLISION_GROUND,
                    InteruptType.LESS,
                    VoiceCategory.GAMEPLAY,
                );
            }
        }
    }

    public animateIn(): void {
        this._isAnimating = true;
        this._sprite.position.set(
            -Application.instance.size.x * 0.5,
            600,
        );

        if (this._posTween !== undefined) { this._posTween.kill(); }
        this._posTween = new TimelineLite();
        this._posTween.to(this._sprite, 3.0, {
            y: 0,
            ease: Power3.easeOut,
        }, 0);
        this._posTween.to(this._sprite, 3.0, {
            x: 0,
            ease: Sine.easeOut,
        }, 0);
        this._posTween.call(() => {
            this._isAnimating = false;
        });
    }

    public animateOut(): void {
        this._isAnimating = true;
        this._active = false;

        if (this._posTween !== undefined) { this._posTween.kill(); }
        this._posTween = new TimelineLite();
        this._posTween.to(this, 3.0, {
            x: 25,
            ease: Sine.easeInOut,
        });
        this._posTween.to(this, 3.0, {
            y: 215,
            ease: Sine.easeInOut,
        }, 0);
        this._posTween.call(() => {
            this.playSfx(SFX.BALLOON_LAND);
        });
    }

    public animateDamage(): void {
        // Position shake
        if (this._posTween !== undefined) { this._posTween.kill(); }
        this._posTween = new TimelineLite();
        this._posTween.to(this._sprite, 0.08, {
            x: -7,
            ease: Sine.easeOut,
        });
        this._posTween.to(this._sprite, 0.13, {
            x: 6,
            ease: Sine.easeInOut,
        });
        this._posTween.to(this._sprite, 0.11, {
            x: -5,
            ease: Sine.easeInOut,
        });
        this._posTween.to(this._sprite, 1.5, {
            x: 0,
            ease: Elastic.easeOut,
        });
        // Heart animation
        if (this._heartTween !== undefined) { this._heartTween.kill(); }
        this._heartLeft.position.set(0, 0);
        this._heartRight.position.set(0, 0);
        this._heartLeft.rotation = 0;
        this._heartRight.rotation = 0;
        this._heartContainer.alpha = 0;
        this._heartContainer.scale.set(0);
        this._heartContainer.visible = true;

        this._heartTween = new TimelineLite();
        this._heartTween.to(this._heartContainer, 0.25, {
            alpha: 1,
            ease: Sine.easeInOut,
        }, 0);
        this._heartTween.to(this._heartContainer.scale, 0.25, {
            x: 1,
            y: 1,
            ease: Sine.easeInOut,
        }, 0);

        this._heartTween.to(this._heartLeft, 0.9, {
            rotation: -0.3 + (Math.random() - 0.5) * 0.2,
            x: -25 + (Math.random() - 0.5) * 10,
            ease: Sine.easeOut,
        }, 0.2);
        this._heartTween.to(this._heartLeft, 0.9, {
            y: 40 + (Math.random() - 0.5) * 20,
            ease: Sine.easeIn,
        }, 0.2);

        this._heartTween.to(this._heartRight, 0.9, {
            rotation: 0.3 + (Math.random() - 0.5) * 0.2,
            x: 25 + (Math.random() - 0.5) * 10,
            ease: Sine.easeOut,
        }, 0.2);
        this._heartTween.to(this._heartRight, 0.9, {
            y: 40 + (Math.random() - 0.5) * 20,
            ease: Sine.easeIn,
        }, 0.2);

        this._heartTween.to(this._heartContainer, 0.25, {
            alpha: 0,
            ease: Sine.easeInOut,
        }, 0.75);
        this._heartTween.call(() => {
            this._heartContainer.visible = false;
        });
    }

    public prepForEnd(): void {
        if (this._speedBoostDelay !== undefined) { this._speedBoostDelay.kill(); }
        PubSub.publishSync(Topics.SPEED_BOOST_END, undefined);
        this.modifySize(Balloon.SCALE_NORM, this._colliderNorm);
    }

    private modifyHealth(pDelta: number): void {
        const newHealth: number = HLF.MathUtils.clamp(this._health + pDelta, 0, Balloon.HEALTH_MAX);
        if (newHealth !== this._health) {
            if (newHealth < this._health) {
                if (newHealth === 0) {
                    this._active = false;
                    PubSub.publishSync(Topics.HEALTH_DEPLETED, undefined);
                }
                else {
                    this.setInvulnerable(Balloon.DAMAGE_INVULN_DUR, InvulnType.DAMAGE);
                }
            }
            this._health = newHealth;
            PubSub.publishSync(Topics.UPDATE_HEALTH, this._health / Balloon.HEALTH_MAX);
        }
    }

    private onParamChanged(pMessage: string, pToken: BalloonControlToken): void {
        switch (pToken.parameter) {
            case BalloonParameter.LIFT:
                this._lift = pToken.value;
                break;
            case BalloonParameter.GRAVITY:
                this._gravity = pToken.value;
                break;
            case BalloonParameter.MIN_SPEED:
                this._speedMin = pToken.value;
                break;
            case BalloonParameter.MAX_SPEED:
                this._speedMax = pToken.value;
                break;
        }
    }

    private modifySize(pScale: number, pCollider: BoxCollider, pDur?: number): void {
        if (this._sizeModTween !== undefined) {
            this._sizeModTween.kill();
        }
        this._sizeModTween = TweenLite.to(this.scale, 0.5, {
            x: pScale,
            y: pScale,
            ease: Sine.easeInOut,
        });
        this._collider = pCollider;

        if (pDur !== undefined) {
            if (this._sizeModDelay !== undefined) {
                this._sizeModDelay.kill();
            }
            this._sizeModDelay = new TimelineLite();
            this._sizeModDelay.call(() => {
                this.playSfx(SFX.POWERUP_SHRINK_END);
                this.modifySize(Balloon.SCALE_NORM, this._colliderNorm);
            }, undefined, this, pDur);
        }
    }

    private setInvulnerable(pDur: number, pType: InvulnType): void {
        this._invulnerable = true;
        if (this._invulnTween !== undefined) {
            this._invulnTween.kill();
            this._sprite.alpha = 1;
        }
        if (pType === InvulnType.DAMAGE) {
            this._invulnTween = new TimelineMax({ repeat: 5, yoyo: true });
            this._invulnTween.to(this._sprite, pDur / 6, {
                alpha: 0.5,
                ease: Sine.easeInOut,
            });
            this._invulnTween.eventCallback("onComplete", () => { this._invulnerable = false; });
        }
        else {
            this._sparkles.emit = true;
            this._invulnTween = new TimelineMax();
            this._invulnTween.call(() => {
                this._sparkles.emit = false;
                this._invulnerable = false;
            }, undefined, this, pDur);
        }
    }

    private boostSpeed(): void {
        PubSub.publishSync(Topics.SPEED_BOOST_START, undefined);
        if (this._speedBoostDelay !== undefined) { this._speedBoostDelay.kill(); }
        this._speedBoostDelay = new TimelineLite();
        this._speedBoostDelay.call(() => {
            PubSub.publishSync(Topics.SPEED_BOOST_END, undefined);
        }, undefined, this, Balloon.SPEED_BOOST_DUR);
    }

    private playSfx(pId: string): void {
        Application.instance.audio.play(pId, 1, false, HLF.AudioCategory.SFX);
    }

    private startLift(): void {
        this._isLifting = true;

        if (this._flameTween !== undefined) { this._flameTween.kill(); }
        this._flameTween = TweenLite.to(this._flame, 0.3, {
            alpha: 1,
            ease: Sine.easeInOut,
        });
    }

    private stopLift(): void {
        this._isLifting = false;

        if (this._flameTween !== undefined) { this._flameTween.kill(); }
        this._flameTween = TweenLite.to(this._flame, 0.3, {
            alpha: 0,
            ease: Sine.easeInOut,
        });
    }
}
